// オブジェクト単体／複数のドラッグ移動ハンドラ。
//
// 振る舞い:
// - pointerdown:
//   - Ctrl / Meta 押下時は選択集合をトグル（既選択なら外す、未選択なら追加）。
//     ドラッグは行わず、複数選択の構築のみ行う。
//   - 既に複数選択されているオブジェクトを通常クリックした場合は、その複数選択を維持して
//     グループドラッグを開始する。
//   - それ以外は単一選択へ置換し、通常のドラッグを開始する。
//   stage 側へのイベント伝播は止めて、空白部のパン開始と競合しないようにする。
// - pointermove: ドラッグ中はオブジェクト要素（複数選択時は全要素）の left/top を直接書き換えて
//   プレビューする。doc store / 共同編集サーバへは送らない。
// - pointerup: ドラッグ距離が閾値以上なら、各オブジェクトの最終位置を transformObject op として送信。
//   閾値未満（=ただのクリック）なら何もしない。
//
// 編集中（contentEditable のテキスト入力）はドラッグを開始しない（pointerdown を見送る）。

import { useEffect, useRef, type RefObject } from "react";
import { CANVAS_HEIGHT_PX, CANVAS_WIDTH_PX } from "@refinement-canvas/domain";
import { CANVAS_SNAP_PX } from "../../../lib/canvas-coordinates";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { useCanvasStore } from "../store/canvas-store";
import type { CanvasOpDispatcher } from "../use-canvas-op-dispatcher";

interface Options {
  readonly objectId: string;
  readonly elementRef: RefObject<HTMLElement | null>;
  readonly dispatcher: CanvasOpDispatcher;
  readonly initialX: number;
  readonly initialY: number;
  readonly initialW: number;
  readonly initialH: number;
  readonly initialRotation?: number | undefined;
  /** 編集中はドラッグを抑止する（テキスト編集中など）。 */
  readonly disabled?: boolean | undefined;
}

const CLICK_THRESHOLD_PX = 3;

// グループドラッグ対象のオブジェクトごとの初期状態と DOM 参照。
interface GroupTarget {
  readonly id: string;
  readonly element: HTMLElement;
  readonly baseLeft: number;
  readonly baseTop: number;
  readonly baseWidth: number;
  readonly baseHeight: number;
}

interface DragState {
  startClientX: number;
  startClientY: number;
  baseLeft: number;
  baseTop: number;
  baseWidth: number;
  baseHeight: number;
  baseTransform: string;
  moved: boolean;
  /** グループドラッグ対象（自分以外）。単一選択時は空配列。 */
  groupTargets: readonly GroupTarget[];
}

export function useObjectDrag(options: Options): void {
  const { objectId, elementRef, dispatcher, initialX, initialY, initialW, initialH, disabled } =
    options;

  // initialX / initialY が effect の依存に含まれるため、他者編集や自分の op.commit
  // エコーで props が更新されるたびに effect が再走する。dragState を effect の
  // closure ローカルに置くと、再走時にドラッグが「中断」されてしまうため、
  // 親 hook 全体で永続する ref に格納しておく。
  const dragStateRef = useRef<DragState | null>(null);
  // initialX/Y/W/H も ref 化しておき、最新の prop 値を listener から参照できるようにする。
  // これにより effect の依存から initialX/Y/W/H を外して再走を抑制できる。
  const baseRef = useRef({ x: initialX, y: initialY, w: initialW, h: initialH });
  baseRef.current = { x: initialX, y: initialY, w: initialW, h: initialH };

  useEffect(() => {
    if (disabled) return undefined;
    const el = elementRef.current;
    if (!el) return undefined;

    const onPointerDown = (event: PointerEvent): void => {
      // 編集対象がコンテナ自体ではない（contentEditable などへの transit）の場合はスキップ。
      const target = event.target as HTMLElement | null;
      if (target && target !== el && target.isContentEditable) return;
      // 主ボタン以外（右クリック等）はドラッグしない
      if (event.button !== 0) return;

      // ロックされたオブジェクトは選択のみ許可しドラッグはしない。
      const docObject = useCanvasDocStore.getState().objects[objectId];
      if (docObject?.locked) {
        useCanvasStore.getState().setSelectedObjectIds([objectId]);
        event.stopPropagation();
        return;
      }

      const additive = event.ctrlKey || event.metaKey;
      const currentSelected = useCanvasStore.getState().selectedObjectIds;

      if (additive) {
        // Ctrl/Meta + クリック: 選択集合をトグル。ドラッグはせず、選択操作のみ。
        const set = new Set(currentSelected);
        if (set.has(objectId)) {
          set.delete(objectId);
        } else {
          set.add(objectId);
        }
        useCanvasStore.getState().setSelectedObjectIds(Array.from(set));
        event.stopPropagation();
        return;
      }

      // 既に複数選択されている対象を通常クリックした場合は選択を維持しグループドラッグへ。
      const isAlreadyMultiSelected =
        currentSelected.length > 1 && currentSelected.includes(objectId);
      if (!isAlreadyMultiSelected) {
        // 単一選択へ置換
        useCanvasStore.getState().setSelectedObjectIds([objectId]);
      }

      // グループドラッグ対象を収集（自分自身は groupTargets には含めず baseLeft/baseTop で別管理）。
      const groupTargets: GroupTarget[] = [];
      if (isAlreadyMultiSelected) {
        const docObjects = useCanvasDocStore.getState().objects;
        for (const id of currentSelected) {
          if (id === objectId) continue;
          const obj = docObjects[id];
          // ロック中オブジェクトはグループドラッグの追従対象から除外する。
          // 選択集合自体は保ったままで、移動だけが起きないようにする。
          if (!obj || obj.locked) continue;
          const node = document.querySelector<HTMLElement>(`[data-testid="canvas-object-${id}"]`);
          if (!node) continue;
          groupTargets.push({
            id,
            element: node,
            baseLeft: obj.x,
            baseTop: obj.y,
            baseWidth: obj.w,
            baseHeight: obj.h,
          });
        }
      }

      dragStateRef.current = {
        startClientX: event.clientX,
        startClientY: event.clientY,
        baseLeft: baseRef.current.x,
        baseTop: baseRef.current.y,
        baseWidth: baseRef.current.w,
        baseHeight: baseRef.current.h,
        baseTransform: el.style.transform,
        moved: false,
        groupTargets,
      };
      el.setPointerCapture(event.pointerId);
      // stage 側のパン開始へ届かないようにする
      event.stopPropagation();
    };

    const onPointerMove = (event: PointerEvent): void => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      const distance = Math.hypot(
        event.clientX - dragState.startClientX,
        event.clientY - dragState.startClientY
      );
      // 閾値未満の手ブレでは left/top を一切書き換えない（クリックの意図を保護する）。
      if (!dragState.moved && distance <= CLICK_THRESHOLD_PX) return;
      dragState.moved = true;

      const view = useCanvasStore.getState().view;
      // クライアント座標差分はステージ座標では zoom で割る必要がある
      const rawDx = (event.clientX - dragState.startClientX) / view.zoom;
      const rawDy = (event.clientY - dragState.startClientY) / view.zoom;
      const { dx, dy } = clampDragDeltaToCanvas(dragState, rawDx, rawDy);
      // CSS で位置を即時反映（DOM の left/top を直接動かしてプレビュー）
      const nextLeft = dragState.baseLeft + dx;
      const nextTop = dragState.baseTop + dy;
      el.style.left = `${nextLeft}px`;
      el.style.top = `${nextTop}px`;
      // 複数選択時は他のオブジェクトも同じ差分で同時にプレビュー移動する。
      for (const target of dragState.groupTargets) {
        target.element.style.left = `${target.baseLeft + dx}px`;
        target.element.style.top = `${target.baseTop + dy}px`;
      }
      // 単一選択時のみ選択ボックス（リサイズ／回転ハンドル）も追従させる。
      if (dragState.groupTargets.length === 0) {
        updateSelectionBoxPreview({ x: nextLeft, y: nextTop });
      }
    };

    const onPointerUp = (event: PointerEvent): void => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      const view = useCanvasStore.getState().view;
      const rawDx = (event.clientX - dragState.startClientX) / view.zoom;
      const rawDy = (event.clientY - dragState.startClientY) / view.zoom;
      const moved = dragState.moved;
      dragStateRef.current = null;
      if (el.hasPointerCapture(event.pointerId)) {
        el.releasePointerCapture(event.pointerId);
      }
      if (moved) {
        // 最終位置を doc store / サーバへ反映。
        // グリッド吸着 ON のときは CANVAS_SNAP_PX で位置を丸める（ペンは別経路なので影響しない）。
        const { gridSnapEnabled } = useCanvasStore.getState();
        const { dx, dy } = clampDragDeltaToCanvas(dragState, rawDx, rawDy);
        const finalize = (frame: MovableFrame): { x: number; y: number } => {
          const rawX = frame.baseLeft + dx;
          const rawY = frame.baseTop + dy;
          const snapped = gridSnapEnabled
            ? { x: snapToGrid(rawX), y: snapToGrid(rawY) }
            : { x: rawX, y: rawY };
          return clampPositionToCanvas({ ...frame, x: snapped.x, y: snapped.y });
        };
        const selfFinal = finalize(dragState);
        if (gridSnapEnabled) {
          el.style.left = `${selfFinal.x}px`;
          el.style.top = `${selfFinal.y}px`;
        }
        if (dragState.groupTargets.length === 0) {
          updateSelectionBoxPreview({ x: selfFinal.x, y: selfFinal.y });
          // 単一選択：従来通り transformObject を 1 回呼ぶ。
          dispatcher.transformObject(objectId, selfFinal);
        } else {
          // 複数選択：自分とグループ全体の transform を 1 つの履歴エントリにまとめる
          // ことで、Ctrl+Z 1 回で全オブジェクトの相対位置がそろって戻るようにする。
          const updates: {
            readonly objectId: string;
            readonly transform: { x: number; y: number };
          }[] = [{ objectId, transform: selfFinal }];
          for (const target of dragState.groupTargets) {
            const finalPos = finalize(target);
            if (gridSnapEnabled) {
              target.element.style.left = `${finalPos.x}px`;
              target.element.style.top = `${finalPos.y}px`;
            }
            updates.push({ objectId: target.id, transform: finalPos });
          }
          dispatcher.transformObjects(updates);
        }
      }
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);
    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
    };
    // 注意: initialX / initialY / initialW / initialH / initialRotation を依存から外している。最新値は baseRef
    // 経由で listener から参照するため、effect 再走による listener 張り直しは不要。
    // （ドラッグ中に他者編集で props が変動しても自分のドラッグが中断されないようにする。）
  }, [objectId, elementRef, dispatcher, disabled]);
}

interface MovableFrame {
  readonly baseLeft: number;
  readonly baseTop: number;
  readonly baseWidth: number;
  readonly baseHeight: number;
}

function clampDragDeltaToCanvas(
  dragState: DragState,
  dx: number,
  dy: number
): { readonly dx: number; readonly dy: number } {
  const frames: MovableFrame[] = [dragState, ...dragState.groupTargets];
  const minDx = Math.max(...frames.map((frame) => -frame.baseLeft));
  const maxDx = Math.min(
    ...frames.map((frame) => CANVAS_WIDTH_PX - (frame.baseLeft + frame.baseWidth))
  );
  const minDy = Math.max(...frames.map((frame) => -frame.baseTop));
  const maxDy = Math.min(
    ...frames.map((frame) => CANVAS_HEIGHT_PX - (frame.baseTop + frame.baseHeight))
  );
  return {
    dx: clampToRange(dx, minDx, maxDx),
    dy: clampToRange(dy, minDy, maxDy),
  };
}

function clampPositionToCanvas(frame: MovableFrame & { readonly x: number; readonly y: number }): {
  readonly x: number;
  readonly y: number;
} {
  return {
    x: clampToRange(frame.x, 0, CANVAS_WIDTH_PX - frame.baseWidth),
    y: clampToRange(frame.y, 0, CANVAS_HEIGHT_PX - frame.baseHeight),
  };
}

function updateSelectionBoxPreview(next: {
  readonly x?: number | undefined;
  readonly y?: number | undefined;
}): void {
  const box = document.querySelector<HTMLElement>('[data-testid="canvas-selection-box"]');
  if (!box) return;
  if (next.x !== undefined) box.style.left = `${next.x}px`;
  if (next.y !== undefined) box.style.top = `${next.y}px`;
}

function snapToGrid(value: number): number {
  return Math.round(value / CANVAS_SNAP_PX) * CANVAS_SNAP_PX;
}

function clampToRange(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  if (max < min) return 0;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}
